import React from 'react';
import { getRefreshToken } from '../../redux/auth/authSelectors';
import submitted from '../../assets/submitted-3d.png';
import { useSelector } from '../../redux/store';
import { getTranslations } from '../../redux/form/formSelectors';
import { getAppVersion, getIsNativeApp } from '../../common/helpers';
import {
  getDeeplinkScheme,
  getLinkToAppStore,
  getLinkToHuaweiStore,
  getLinkToAppStoreForAutoLogin,
} from '../../common/environment';
import { Button } from '../Button';
import { DeviceData } from '../../common/deviceData';
import { ButtonLink } from '../ButtonLink';

interface ButtonProps {
  buttonLabel: string;
}

interface ContinueInAppButtonProps extends ButtonProps {
  showDownloadAppCallback: () => void;
}

const DownloadAppButton = ({ buttonLabel }: ButtonProps) => {
  const token = useSelector(getRefreshToken);
  const androidIosLink = token
    ? getLinkToAppStoreForAutoLogin(token)
    : getLinkToAppStore();
  const huaweiLink = getLinkToHuaweiStore();

  return (
    <ButtonLink
      href={DeviceData.isHuawei() ? huaweiLink : androidIosLink}
      target="_blank"
      color="bg-green-500"
      label={buttonLabel}
      id="download_app"
    />
  );
};

const ContinueInAppButton = ({
  buttonLabel,
  showDownloadAppCallback,
}: ContinueInAppButtonProps) => {
  const [isRedirectInProgress, setIsRedirectInProgress] = React.useState(false);

  const onClick = React.useCallback(() => {
    setIsRedirectInProgress(true);
    window.location.replace(`${getDeeplinkScheme()}partner-lead-login/`);
    const timeoutMs = 3000;

    // In case deeplink didn't work: fallback to app download
    setTimeout(() => {
      setIsRedirectInProgress(false);
      showDownloadAppCallback();
    }, timeoutMs);
  }, [showDownloadAppCallback]);

  return (
    <Button
      id="continue"
      onClick={onClick}
      color={isRedirectInProgress ? 'bg-green-200' : 'bg-green-500'}
      label={buttonLabel}
      disabled={isRedirectInProgress}
      loading={isRedirectInProgress}
    />
  );
};

const OkCloseButton = ({ buttonLabel }: ButtonProps) => {
  const [isRedirectInProgress, setIsRedirectInProgress] = React.useState(false);

  const onClick = React.useCallback(() => {
    setIsRedirectInProgress(true);
    window.location.replace(`${getDeeplinkScheme()}action/closeBrowser`);
  }, [setIsRedirectInProgress]);

  return (
    <Button
      id="ok_close"
      onClick={onClick}
      color={isRedirectInProgress ? 'bg-green-200' : 'bg-green-500'}
      label={buttonLabel}
      disabled={isRedirectInProgress}
      loading={isRedirectInProgress}
    />
  );
};

enum ButtonMode {
  DOWNLOAD_APP = 'download_app',
  CONTINUE_IN_APP = 'continue_in_app',
  OK_CLOSE = 'ok_close',
}

export const SubmittedFinalScreenForEarlyAppAccess: React.FC = () => {
  const appVersion = getAppVersion();
  const isOpenedFromNativeAppFlow = getIsNativeApp();

  const commonTranslations = useSelector(getTranslations);
  const [isInWebview, setIsInWebview] = React.useState(!!appVersion);

  /**
   * If this screen is opened in a web browser, we show "Download App" button.
   * If this screen is opened inside app, but in a WebView, we show "Continue in App" button.
   * If this screen is opened from app native flow (Lead Homescreen), we show "Close" button.
   */
  let buttonType: ButtonMode = ButtonMode.DOWNLOAD_APP;
  if (isOpenedFromNativeAppFlow) {
    buttonType = ButtonMode.OK_CLOSE;
  } else if (isInWebview) {
    buttonType = ButtonMode.CONTINUE_IN_APP;
  }

  const text =
    buttonType === ButtonMode.DOWNLOAD_APP
      ? commonTranslations.submitted_screen.text_download_app
      : commonTranslations.submitted_screen.text;

  return (
    <div className="flex flex-col h-screen tablet:h-auto">
      <div className="flex flex-col gap-6 pt-6 text-center flex-grow">
        <img
          height={148}
          width={200}
          src={submitted}
          alt={commonTranslations.submitted_screen.title}
          className="mx-auto"
        />
        <div className="flex flex-col gap-4">
          <h1 className="text-2xl font-bold">
            {commonTranslations.submitted_screen.title}
          </h1>
          <div className="text-gray-700 text-sm mt-1">{text}</div>
        </div>
      </div>
      <div className="sticky bottom-0 tablet:static py-4 w-full text-center">
        {buttonType === ButtonMode.OK_CLOSE && (
          <OkCloseButton buttonLabel={commonTranslations.common_actions.ok} />
        )}
        {buttonType === ButtonMode.DOWNLOAD_APP && (
          <DownloadAppButton
            buttonLabel={
              commonTranslations.submitted_screen.download_app_button
            }
          />
        )}
        {buttonType === ButtonMode.CONTINUE_IN_APP && (
          <ContinueInAppButton
            buttonLabel={commonTranslations.submitted_screen.continue_button}
            showDownloadAppCallback={() => setIsInWebview(false)}
          />
        )}
      </div>
    </div>
  );
};
